import React, { useEffect, useState } from "react";
import { API_BASE } from "../api/config";
import "./OfficeDamageApprovalPanel.css";

function formatQty(value) {
  return Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}

export default function OfficeDamageApprovalPanel() {
  const [username, setUsername] = useState("office_admin");
  const [status, setStatus] = useState("PENDING_REVIEW");
  const [rows, setRows] = useState([]);
  const [testForm, setTestForm] = useState({
    sourceLocationCode: "GMP",
    warehouseLocationCode: "DNGPF",
    productCode: "TEST-DMG-001",
    brand: "PRETTYFIT",
    color: "BLACK",
    size: "35",
    qty: 1,
    reason: "Damage from shop",
    conditionNote: "Torn item",
  });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  async function loadRows() {
    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({
        username,
        status,
        limit: "100",
      });

      const response = await fetch(`${API_BASE}/api/erp/damage-requests?${params.toString()}`);
      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to load damage approvals");
      }

      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  async function createTestRequest() {
    try {
      setActionLoading("create");
      setError("");
      setSuccessMsg("");

      const response = await fetch(`${API_BASE}/api/erp/damage-requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username,
          sourceLocationCode: testForm.sourceLocationCode,
          warehouseLocationCode: testForm.warehouseLocationCode,
          reason: testForm.reason,
          requestNote: "Created from Office Damage Approval UI",
          items: [
            {
              productCode: testForm.productCode,
              brand: testForm.brand,
              color: testForm.color,
              size: testForm.size,
              qty: Number(testForm.qty || 0),
              conditionNote: testForm.conditionNote,
            },
          ],
        }),
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Create damage request failed");
      }

      setSuccessMsg(`Damage request created: ${data.damageRequest?.damageNo || ""}`);
      await loadRows();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading("");
    }
  }

  async function approve(row) {
    try {
      setActionLoading(row.id);
      setError("");
      setSuccessMsg("");

      const response = await fetch(`${API_BASE}/api/erp/damage-requests/${row.id}/approve`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username,
          approvalNote: "Approved as damage from Office ERP",
        }),
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Approve damage failed");
      }

      setSuccessMsg(`Approved: ${row.damageNo}`);
      await loadRows();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading("");
    }
  }

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <div className="officeDamageApprovalPanel">
      <div className="officeDamageApprovalPanel__head">
        <div>
          <div className="officeDamageApprovalPanel__tag">⚠️ DAMAGE APPROVAL</div>
          <h2>Office Damage Approval</h2>
          <p>Shop မှလာတဲ့ damage request ကို Office/Admin က approve လုပ်ပြီးမှ warehouse damage register ထဲဝင်မယ်။</p>
        </div>

        <button type="button" onClick={loadRows} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error && <div className="officeDamageAlert officeDamageAlert--error">{error}</div>}
      {successMsg && <div className="officeDamageAlert officeDamageAlert--success">{successMsg}</div>}

      <div className="officeDamageTestBox">
        <h3>Create Test Damage Request</h3>

        <div className="officeDamageFormGrid">
          {Object.keys(testForm).map((key) => (
            <label key={key}>
              <span>{key}</span>
              <input
                value={testForm[key]}
                onChange={(e) =>
                  setTestForm((prev) => ({
                    ...prev,
                    [key]: e.target.value,
                  }))
                }
              />
            </label>
          ))}

          <button type="button" onClick={createTestRequest} disabled={actionLoading === "create"}>
            {actionLoading === "create" ? "Creating..." : "Create Test"}
          </button>
        </div>
      </div>

      <div className="officeDamageFilters">
        <label>
          <span>Approver Username</span>
          <input value={username} onChange={(e) => setUsername(e.target.value)} />
        </label>

        <label>
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="PENDING_REVIEW">Pending Review</option>
            <option value="APPROVED_DAMAGE">Approved Damage</option>
            <option value="RECEIVED_DAMAGE">Received Damage</option>
            <option value="ALL">All</option>
          </select>
        </label>

        <button type="button" onClick={loadRows}>Search</button>
      </div>

      <div className="officeDamageList">
        {rows.length === 0 ? (
          <div className="officeDamageEmpty">No damage request found.</div>
        ) : (
          rows.map((row) => (
            <div key={row.id} className="officeDamageCard">
              <div className="officeDamageCard__top">
                <div>
                  <b>{row.damageNo}</b>
                  <span>{row.reason || "-"}</span>
                </div>

                <strong className={`officeDamageStatus-${row.status}`}>{row.status}</strong>
              </div>

              <div className="officeDamageRoute">
                <span>{row.sourceLocationCode}</span>
                <b>→</b>
                <span>{row.warehouseLocationCode}</span>
              </div>

              <div className="officeDamageMeta">
                <span>Items: {row.itemRows}</span>
                <span>Total Qty: {formatQty(row.totalQty)}</span>
                <span>Requested By: {row.requestedBy || "-"}</span>
                <span>Requested: {formatDate(row.requestedAt)}</span>
              </div>

              <div className="officeDamageItems">
                {(row.items || []).slice(0, 8).map((item) => (
                  <span key={item.id}>
                    {item.productCode} / {item.color || "-"} / {item.size} × {formatQty(item.qty)}
                  </span>
                ))}
              </div>

              <div className="officeDamageActions">
                {row.status === "PENDING_REVIEW" ? (
                  <button type="button" onClick={() => approve(row)} disabled={actionLoading === row.id}>
                    {actionLoading === row.id ? "Approving..." : "✅ Approve Damage"}
                  </button>
                ) : (
                  <span>Office action done</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
